/**
 * Procedural canvas textures for the clean-industrial pass (LOCKED-DESIGN 18.1).
 * The neon "glowing seam" floor is retired: the floor is now a bright matte
 * CONCRETE slab (aggregate grain, panel expansion joints, localized wear and
 * grime), and painted-STEEL panels carry seams, bolt rows, and scuffs. These
 * feed diffuse maps (roughness/metalness on the material do the material
 * identity) rather than emissive mood.
 *
 * Cosmetic randomness is seeded (LOCKED-DESIGN 18.8): every generation is
 * pixel-identical, so screenshot baselines can be frozen. This PRNG is
 * render-side only and stays isolated from gameplay randomness, which uses
 * its own versioned seeded stream (LOCKED-DESIGN 13.1).
 */
import * as THREE from "three";
import { GLASS, TEXTURE_THEME } from "./theme.js";

/** Fixed cosmetic seed — arbitrary, but frozen so textures never vary. */
const COSMETIC_TEXTURE_SEED = 0x54505a31; // "TPZ1"

/** mulberry32: tiny seeded PRNG, uniform in [0, 1). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Bright concrete floor: light aggregate slab, panel expansion joints, wear
 * speckle, and localized grime. The companion "emissive" canvas is near-black
 * (matte concrete) with only faint painted circulation lines, so the retired
 * neon glow does not come back — bloom stays accent-only.
 */
export function makeFloorTextures(): { map: THREE.CanvasTexture; emissive: THREE.CanvasTexture } {
  const rand = mulberry32(COSMETIC_TEXTURE_SEED);
  const theme = TEXTURE_THEME.floor;
  const size = 1024;
  const cell = 128;

  const base = document.createElement("canvas");
  base.width = base.height = size;
  const b = base.getContext("2d")!;
  b.fillStyle = theme.base;
  b.fillRect(0, 0, size, size);
  // per-panel value variation (kept in the light concrete band)
  for (let y = 0; y < size; y += cell) {
    for (let x = 0; x < size; x += cell) {
      const v = theme.panelValue.base + Math.floor(rand() * theme.panelValue.range);
      b.fillStyle = `rgb(${v},${v + 2},${v + 4})`;
      b.fillRect(x + 1, y + 1, cell - 2, cell - 2);
      // concrete aggregate grain: dark aggregate + bright flecks
      b.fillStyle = theme.aggregateDark;
      for (let i = 0; i < 34; i++) b.fillRect(x + rand() * cell, y + rand() * cell, 1 + rand() * 2, 1 + rand() * 2);
      b.fillStyle = theme.aggregateLight;
      for (let i = 0; i < 18; i++) b.fillRect(x + rand() * cell, y + rand() * cell, 1, 1);
    }
  }
  // panel expansion joints (diffuse, darker — real slab seams, not glow)
  b.strokeStyle = theme.seam;
  b.lineWidth = 3;
  for (let i = 0; i <= size; i += cell) {
    b.beginPath(); b.moveTo(i, 0); b.lineTo(i, size); b.stroke();
    b.beginPath(); b.moveTo(0, i); b.lineTo(size, i); b.stroke();
  }
  // localized grime: a handful of soft dark blotches (no visual clutter)
  b.fillStyle = theme.grime;
  for (let i = 0; i < 14; i++) {
    const gx = rand() * size; const gy = rand() * size; const gr = 26 + rand() * 70;
    const grad = b.createRadialGradient(gx, gy, 0, gx, gy, gr);
    grad.addColorStop(0, theme.grime); grad.addColorStop(1, "rgba(0,0,0,0)");
    b.fillStyle = grad; b.beginPath(); b.arc(gx, gy, gr, 0, Math.PI * 2); b.fill();
  }

  const glow = document.createElement("canvas");
  glow.width = glow.height = size;
  const g = glow.getContext("2d")!;
  g.fillStyle = "#000000";
  g.fillRect(0, 0, size, size);
  // faint painted circulation accents only (the seam GLOW is retired)
  g.strokeStyle = theme.accentSeam;
  g.lineWidth = 3;
  for (let i = 0; i < 7; i++) {
    const p = Math.floor(rand() * 8) * cell;
    const horiz = rand() < 0.5;
    const start = Math.floor(rand() * 4) * cell;
    const len = (2 + Math.floor(rand() * 4)) * cell;
    g.beginPath();
    if (horiz) { g.moveTo(start, p); g.lineTo(start + len, p); }
    else { g.moveTo(p, start); g.lineTo(p, start + len); }
    g.stroke();
  }

  const map = new THREE.CanvasTexture(base);
  const emissive = new THREE.CanvasTexture(glow);
  for (const t of [map, emissive]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(4, 4);
    t.anisotropy = 4;
    t.colorSpace = THREE.SRGBColorSpace;
  }
  return { map, emissive };
}

/**
 * Painted-steel panel diffuse map: bright panel field, darker panel seams, a
 * bolt row per panel (dark hole + bright specular pip), light scuffs, and
 * localized grime. Used for the enclosure shell and the sorting island so
 * "painted steel" reads distinct from concrete and from dark machinery.
 */
export function makeSteelTexture(): THREE.CanvasTexture {
  const rand = mulberry32(COSMETIC_TEXTURE_SEED ^ 0x5a);
  const theme = TEXTURE_THEME.steel;
  const size = 512;
  const cell = 128;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const x = c.getContext("2d")!;
  x.fillStyle = theme.base;
  x.fillRect(0, 0, size, size);
  for (let py = 0; py < size; py += cell) {
    for (let px = 0; px < size; px += cell) {
      const v = theme.panelValue.base + Math.floor(rand() * theme.panelValue.range);
      x.fillStyle = `rgb(${v},${v + 2},${v + 5})`;
      x.fillRect(px + 2, py + 2, cell - 4, cell - 4);
      // scuffs
      x.fillStyle = theme.scuff;
      for (let i = 0; i < 8; i++) x.fillRect(px + rand() * cell, py + rand() * cell, 2 + rand() * 14, 1);
      // bolt row along the panel edge
      for (const [bx, by] of [[px + 12, py + 12], [px + cell - 12, py + 12], [px + 12, py + cell - 12], [px + cell - 12, py + cell - 12]]) {
        x.fillStyle = theme.bolt; x.beginPath(); x.arc(bx, by, 3.2, 0, Math.PI * 2); x.fill();
        x.fillStyle = theme.boltHi; x.beginPath(); x.arc(bx - 1, by - 1, 1.1, 0, Math.PI * 2); x.fill();
      }
    }
  }
  // panel seams
  x.strokeStyle = theme.seam;
  x.lineWidth = 2.5;
  for (let i = 0; i <= size; i += cell) {
    x.beginPath(); x.moveTo(i, 0); x.lineTo(i, size); x.stroke();
    x.beginPath(); x.moveTo(0, i); x.lineTo(size, i); x.stroke();
  }
  // localized grime
  for (let i = 0; i < 6; i++) {
    const gx = rand() * size; const gy = rand() * size; const gr = 30 + rand() * 60;
    const grad = x.createRadialGradient(gx, gy, 0, gx, gy, gr);
    grad.addColorStop(0, theme.grime); grad.addColorStop(1, "rgba(0,0,0,0)");
    x.fillStyle = grad; x.beginPath(); x.arc(gx, gy, gr, 0, Math.PI * 2); x.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * Reinforced-glass surface graphics (LOCKED-DESIGN 2.1/18.1). Two seeded canvas
 * layers give the pane real physical presence without any per-frame reflection:
 *  - `overlay` (normal-blended): a laminated edge-tint band, greasy smudges,
 *    settled dust, squeegee streaks, and a few impact star-cracks where direct
 *    throws struck — so the sealed barrier reads as heavy and used.
 *  - `sheen` (additive): a baked diagonal reflection streak, the "cheap,
 *    quality-tiered reflection" that GrayBoxArena drops on the low tier.
 * Both are pixel-identical every run (frozen cosmetic seed) so screenshots stay
 * stable. The wide pane is authored ~6:1, so the canvases match that aspect and
 * cracks/dust map without stretching.
 */
export function makeGlassTextures(): { overlay: THREE.CanvasTexture; sheen: THREE.CanvasTexture } {
  const rand = mulberry32(COSMETIC_TEXTURE_SEED ^ 0x6c);
  const w = 1024;
  const h = 168;

  const over = document.createElement("canvas");
  over.width = w; over.height = h;
  const o = over.getContext("2d")!;
  o.clearRect(0, 0, w, h);
  // laminated edge tint: a cool inner border that fades toward the center
  const edge = 22;
  const inset = o.createLinearGradient(0, 0, 0, h);
  inset.addColorStop(0, GLASS.edge); inset.addColorStop(.14, "rgba(0,0,0,0)");
  inset.addColorStop(.86, "rgba(0,0,0,0)"); inset.addColorStop(1, GLASS.edge);
  o.fillStyle = inset; o.fillRect(0, 0, w, h);
  const sides = o.createLinearGradient(0, 0, w, 0);
  sides.addColorStop(0, GLASS.edge); sides.addColorStop(.05, "rgba(0,0,0,0)");
  sides.addColorStop(.95, "rgba(0,0,0,0)"); sides.addColorStop(1, GLASS.edge);
  o.fillStyle = sides; o.fillRect(0, 0, w, h);
  // greasy smudges (soft dark blotches)
  for (let i = 0; i < 16; i++) {
    const gx = edge + rand() * (w - edge * 2); const gy = rand() * h; const gr = 26 + rand() * 74;
    const grad = o.createRadialGradient(gx, gy, 0, gx, gy, gr);
    grad.addColorStop(0, GLASS.overlay.smudge); grad.addColorStop(1, "rgba(0,0,0,0)");
    o.fillStyle = grad; o.beginPath(); o.arc(gx, gy, gr, 0, Math.PI * 2); o.fill();
  }
  // settled dust along the bottom sill
  o.fillStyle = GLASS.overlay.dust;
  for (let i = 0; i < 260; i++) o.fillRect(rand() * w, h - rand() * rand() * h, 1, 1 + rand());
  // vertical squeegee streaks
  o.strokeStyle = GLASS.overlay.streak; o.lineWidth = 1.5;
  for (let i = 0; i < 9; i++) {
    const sx = edge + rand() * (w - edge * 2);
    o.beginPath(); o.moveTo(sx, 6 + rand() * 20); o.lineTo(sx + (rand() - .5) * 8, h - 6 - rand() * 20); o.stroke();
  }
  // impact star-cracks: where direct throws struck the sealed barrier
  for (let i = 0; i < 3; i++) {
    const cx = w * (.3 + rand() * .4); const cy = h * (.34 + rand() * .4); const spokes = 6 + Math.floor(rand() * 4);
    o.strokeStyle = GLASS.overlay.impact;
    for (let s = 0; s < spokes; s++) {
      const a = (s / spokes) * Math.PI * 2 + rand() * .4; const len = 10 + rand() * 26;
      o.lineWidth = .6 + rand() * 1.1;
      o.beginPath(); o.moveTo(cx, cy); o.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len); o.stroke();
    }
    for (const r of [4 + rand() * 3, 9 + rand() * 5]) {
      o.strokeStyle = GLASS.overlay.impact; o.lineWidth = .8;
      o.beginPath(); o.arc(cx, cy, r, 0, Math.PI * 2); o.stroke();
    }
    o.fillStyle = GLASS.overlay.impactCore; o.beginPath(); o.arc(cx, cy, 1.6, 0, Math.PI * 2); o.fill();
  }

  const sh = document.createElement("canvas");
  sh.width = w; sh.height = h;
  const s = sh.getContext("2d")!;
  s.clearRect(0, 0, w, h);
  // baked diagonal reflection streaks (the tiered "reflection" read)
  for (const [bx, bw] of [[w * .18, 90], [w * .56, 150]] as const) {
    s.save(); s.translate(bx, 0); s.transform(1, 0, -.5, 1, 0, 0);
    const grad = s.createLinearGradient(0, 0, bw, 0);
    grad.addColorStop(0, "rgba(0,0,0,0)"); grad.addColorStop(.5, GLASS.sheen.band); grad.addColorStop(1, "rgba(0,0,0,0)");
    s.fillStyle = grad; s.fillRect(0, -h, bw, h * 3); s.restore();
  }

  const overlay = new THREE.CanvasTexture(over);
  const sheen = new THREE.CanvasTexture(sh);
  for (const t of [overlay, sheen]) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; }
  return { overlay, sheen };
}

/** Diagonal hazard stripes (orange/near-black) for the maw lip. */
export function makeHazardTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 64;
  const x = c.getContext("2d")!;
  x.fillStyle = TEXTURE_THEME.hazard.base;
  x.fillRect(0, 0, 256, 64);
  x.fillStyle = TEXTURE_THEME.hazard.stripe;
  for (let i = -64; i < 256 + 64; i += 48) {
    x.beginPath();
    x.moveTo(i, 64); x.lineTo(i + 24, 64); x.lineTo(i + 24 + 32, 0); x.lineTo(i + 32, 0);
    x.closePath(); x.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(6, 1);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
